<template>
  <component
    :is="to ? NuxtLink : 'button'"
    :to="to ? to : undefined"
    :type="type ? type : !to ? 'button' : undefined"
    :class="[
      'group relative inline-flex items-center justify-center text-center font-black uppercase leading-none text-white no-underline',
      {
        'cursor-not-allowed': disabled || loading,
        'h-[172px] w-[172px] bg-[length:230px_auto] text-2xl': size === 'sm',
        'h-[220px] w-[220px] bg-[length:282px_auto] text-3xl': size === 'md',
        'h-[460px] w-[460px] bg-[length:606px_auto] text-3xl': size === 'lg'
      }
    ]"
    :disabled="disabled"
    @click="onClick"
    ><span ref="ctxRef" class="flex h-full items-center">
      <span
        class="fx-frame pointer-events-none absolute left-1/2 top-1/2 block origin-center -translate-x-1/2 -translate-y-1/2 bg-center bg-no-repeat"
        :class="{
          'transition-transform duration-300 ease-quart-out group-hover:scale-105': !isAnimating,
          'h-[172px] w-[172px] bg-glass bg-[length:230px_auto]': size === 'sm',
          'h-[220px] w-[220px] bg-glass bg-[length:282px_auto]': size === 'md',
          'h-[460px] w-[460px] bg-glass-large bg-[length:606px_auto]': size === 'lg'
        }"
      ></span>
      <span
        class="fx-inner pointer-events-none absolute left-1/2 top-1/2 block origin-center -translate-x-1/2 -translate-y-1/2 rotate-45 border border-white border-opacity-50"
        :class="{
          'h-[106px] w-[106px] rounded-[36px]': size === 'sm',
          'h-[132px] w-[132px] rounded-[36px]': size === 'md',
          'h-[292px] w-[292px] rounded-[98px]': size === 'lg'
        }"
      ></span>
      <span
        class="fx-inner pointer-events-none absolute left-1/2 top-1/2 block origin-center -translate-x-1/2 -translate-y-1/2 rotate-45 border border-[#adadad] bg-white bg-opacity-10 backdrop-blur-lg"
        :class="{
          'h-[104px] w-[104px] rounded-[36px]': size === 'sm',
          'h-[130px] w-[130px] rounded-[36px]': size === 'md',
          'h-[290px] w-[290px] rounded-[98px]': size === 'lg'
        }"
      ></span>
      <span class="fx-text relative flex h-full w-full items-center justify-center">
        <span
          class="inline-flex self-center"
          :class="{
            'px-7': size === 'sm',
            'px-6': size === 'md',
            'px-2': size === 'lg'
          }"
        >
          {{ label }}
          <slot />
        </span>
        <svg
          v-if="variant === 'arrow'"
          width="200"
          height="124"
          viewBox="0 0 200 124"
          fill="none"
          class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 will-change-transform"
          :class="{
            'h-auto w-18': size === 'md'
          }"
        >
          <g filter="url(#filter0_d_1016_4116)">
            <path
              d="M5.37695 58.8767H194.377"
              stroke="#F6F6F6"
              stroke-width="3"
              stroke-linecap="round"
            />
            <path
              d="M137.102 1.60132L194.377 58.877L137.102 116.153"
              stroke="#F6F6F6"
              stroke-width="3"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_1016_4116"
              x="0.876953"
              y="0.540771"
              width="198.621"
              height="122.673"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="3" />
              <feGaussianBlur stdDeviation="1.5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_1016_4116"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_1016_4116"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { NuxtLink } from '#components'
import type { RouteLocationRaw } from 'vue-router'
import type { NuxtLinkProps } from 'nuxt/app'
import type { IconSize, IconName } from '@/components/media/Icon.vue'

export type ButtonGlassVariant = 'text' | 'arrow'

interface ButtonGlassProps {
  disabled?: boolean
  inactive?: boolean
  icon?: {
    color?: string
    name: IconName
    size?: IconSize
  }
  variant?: ButtonGlassVariant
  label?: string | null
  loading?: boolean
  to?: RouteLocationRaw | NuxtLinkProps['to']
  prevent?: boolean
  size?: 'sm' | 'md' | 'lg'
  type?: HTMLButtonElement['type'] | undefined
  entryDelay?: number
}

const props = withDefaults(defineProps<ButtonGlassProps>(), {
  to: undefined,
  disabled: false,
  inactive: false,
  variant: 'text',
  loading: false,
  icon: undefined,
  label: null,
  prevent: false,
  size: 'md',
  type: undefined,
  entryDelay: 0
})

const emit = defineEmits<{
  (event: 'click', e: MouseEvent): void
}>()

const onClick = (event: MouseEvent) => {
  if (props.disabled || props.loading || props.prevent) {
    event.preventDefault()
    event.stopPropagation()
  }
  emit('click', event)
}

const ctxRef = ref<HTMLElement | null>(null)
const isAnimating = ref(false)

let ctx: gsap.Context | null = null
let tl: GSAPTimeline | null = null

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    ctx = gsap.context(() => {
      gsap.set(['.fx-frame, .fx-inner, .fx-text'], { scale: 0 })

      tl = gsap.timeline({
        delay: props.entryDelay,
        scrollTrigger: {
          trigger: ctxRef.value,
          start: 'center bottom'
        }
      })
      tl.to(['.fx-frame, .fx-inner, .fx-text'], {
        duration: 0.74,
        scale: 1,
        ease: 'back.out(1.2)',
        onStart: () => {
          isAnimating.value = true
        },
        onComplete: () => {
          gsap.set(['.fx-frame, .fx-inner, .fx-text'], { clearProps: 'all' })
          isAnimating.value = false
        }
      })
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>
